import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export default function RolesPage() {
  const api = useApi();
  const { me } = useMe();
  const isSuper = me.user.role === "super_admin";
  const [d, setD] = useState(null);
  const [edits, setEdits] = useState({});
  const [newName, setNewName] = useState("");
  const load = useCallback(() => api.get("/admin/roles").then((r) => { setD(r.data); setEdits({}); }).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const adminRoles = d.roles.filter((r) => r.is_admin);
  const permsOf = (r) => edits[r.key] ?? r.permissions;
  const toggle = (r, p) => { const cur = permsOf(r); setEdits({ ...edits, [r.key]: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] }); };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader eyebrow="Admin Control Centre" title="Roles & permissions" />
      {!isSuper && <p className="mb-4 text-sm text-slate-700">Only a Super Admin can change roles and permissions.</p>}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="roles-matrix">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-600">
            <tr><th className="px-3 py-3">Permission</th>{adminRoles.map((r) => <th key={r.key} className="px-2 py-3 text-center">{r.name}<div className="font-normal">{r.users} user(s)</div></th>)}</tr>
          </thead>
          <tbody>
            {Object.entries(d.permissions).map(([p, label]) => (
              <tr key={p} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-800">{label}</td>
                {adminRoles.map((r) => (
                  <td key={r.key} className="px-2 py-2 text-center">
                    <Checkbox checked={r.key === "super_admin" || permsOf(r).includes(p)} disabled={!isSuper || r.key === "super_admin"} onCheckedChange={() => toggle(r, p)} aria-label={`${r.name}: ${label}`} data-testid={`perm-${r.key}-${p}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isSuper && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.keys(edits).map((k) => { const r = adminRoles.find((x) => x.key === k); return (
            <Button key={k} onClick={() => run(() => api.put(`/admin/roles/${k}`, { name: r.name, permissions: edits[k] }), `${r.name} saved`)} className="bg-[#222d99] hover:bg-[#1a237e]" data-testid={`role-save-${k}`}>Save {r.name}</Button>); })}
        </div>
      )}
      {isSuper && (
        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Custom roles</h2>
          <ul className="mt-2 space-y-1 text-sm">{adminRoles.filter((r) => !r.builtin).map((r) => (
            <li key={r.key} className="flex items-center gap-2">{r.name}<button type="button" aria-label={`Delete ${r.name}`} onClick={() => window.confirm(`Delete role ${r.name}?`) && run(() => api.delete(`/admin/roles/${r.key}`), "Role deleted")} className="rounded p-1 text-slate-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button></li>))}</ul>
          <div className="mt-3 flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New role name" aria-label="New role name" className="max-w-xs" data-testid="new-role-name" />
            <Button variant="outline" disabled={newName.trim().length < 2} onClick={() => run(async () => { await api.post("/admin/roles", { name: newName, permissions: [] }); setNewName(""); }, "Role created")} data-testid="new-role-btn">Create role</Button>
          </div>
        </section>
      )}
    </div>
  );
}
